// src/pages/lab/tracking/components/sampleModalComponents/GenotyperSection.tsx
// Read-only CNV / PGx / mtDNA-CN results for one sample, with the genotyper run they came from.

import React from "react";
import { Alert, Button, Descriptions, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import axios from "axios";

import { DataTable, EmptyState, InlineLoading, SectionCard, StatusTag } from "../../../../../components/shared";

import type {
	GenotyperCnvStatus,
	GenotyperPhaseStatus,
	SampleGenotyper,
	SampleGenotyperCnv,
	SampleGenotyperMtdna,
	SampleGenotyperPgx,
	SampleGenotyperRunSummary,
} from "../../../../../types/lab/GenotyperTypes";

import { useSampleGenotyper } from "../../../../../hooks/genetic/useSampleGenotyper";
import { GENOTYPER_RUN_STATUS_LABELS } from "../../../../../utils/genotyper";
import { formatInZone } from "../../../../../utils/time";

const { Text } = Typography;

interface GenotyperSectionProps {
	sampleId: string;
}

export const EMPTY_TITLE = "No raw genotyper results for this sample yet.";
export const LEGACY_NOTE = "legacy import — no run record";
// Decision 5 (2026-09-21): the CNV block must say WHY there are no rows, not just be empty.
export const NO_SIGNAL_MESSAGE =
	"No CNV results — the array signal for this sample was unusable (NaN LRR/BAF). PGx and mtDNA results below are unaffected.";
// Decision 4 (2026-09-21): scaled/PCA values are batch-relative; the raw ratio leads.
export const BATCH_RELATIVE_NOTE =
	"Scaled/PCA values are z-scored against the other samples on the same chip — not comparable across runs and not calibrated for clinical use.";

// The same "actionable" heuristic pgx_report.py prints: one of these terms and not "normal".
const ACTIONABLE_PHENOTYPE = /poor|intermediate|rapid|ultra/i;
export const isActionablePhenotype = (phenotype: string | null | undefined): boolean =>
	!!phenotype && ACTIONABLE_PHENOTYPE.test(phenotype) && !/normal/i.test(phenotype);

export const isNotTested = (pgx: SampleGenotyperPgx): boolean => (pgx.phenotype ?? "").trim().toLowerCase() === "not tested";

const CN_COLOR: Record<number, string> = {
	0: "var(--mg-danger)",
	1: "var(--mg-warning)",
	3: "var(--mg-info)",
	4: "var(--mg-info)",
};

const phenotypeColor = (phenotype: string): string => (/poor|ultra/i.test(phenotype) ? "var(--mg-danger)" : "var(--mg-warning)");

const PHASE_HINT: Record<GenotyperPhaseStatus | GenotyperCnvStatus, string | undefined> = {
	pending: "The pipeline has not started for this sample yet.",
	running: "The pipeline is still running for this sample.",
	failed: "The pipeline failed for this sample.",
	skipped: "The pipeline was skipped for this sample.",
	completed: undefined,
	no_signal: undefined,
};

const phaseHint = (status: GenotyperPhaseStatus | GenotyperCnvStatus | null | undefined): string | undefined =>
	status ? PHASE_HINT[status] : undefined;

const formatCn = (value: number | null | undefined): string =>
	typeof value === "number" && Number.isFinite(value) ? value.toLocaleString("en-AU", { maximumFractionDigits: 2 }) : "—";

const formatCount = (value: number | null | undefined): string =>
	typeof value === "number" && Number.isFinite(value) ? value.toLocaleString("en-AU") : "—";

const cnvColumns: ColumnsType<SampleGenotyperCnv> = [
	{
		title: "Gene",
		dataIndex: "gene",
		key: "gene",
		width: 120,
		render: (gene: string) => <Text style={{ fontSize: 12, fontWeight: 500 }}>{gene}</Text>,
	},
	{
		title: "Copy number",
		dataIndex: "copy_number",
		key: "copy_number",
		width: 120,
		render: (cn: number) =>
			cn === 2 ? (
				<Text style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>{cn}</Text>
			) : (
				<StatusTag status={`cn-${cn}`} label={String(cn)} color={CN_COLOR[cn] ?? "var(--mg-warning)"} size="small" />
			),
	},
	{
		title: "Description",
		dataIndex: "cn_description",
		key: "cn_description",
		render: (description: string, row: SampleGenotyperCnv) => (
			<Text style={{ fontSize: 12, fontWeight: row.copy_number === 2 ? undefined : 500 }}>{description || "—"}</Text>
		),
	},
];

const pgxColumns: ColumnsType<SampleGenotyperPgx> = [
	{
		title: "Gene",
		dataIndex: "gene",
		key: "gene",
		width: 120,
		render: (gene: string, row: SampleGenotyperPgx) => (
			<Text type={isNotTested(row) ? "secondary" : undefined} style={{ fontSize: 12, fontWeight: 500 }}>
				{gene}
			</Text>
		),
	},
	{
		title: "Diplotype",
		dataIndex: "diplotype",
		key: "diplotype",
		width: 220,
		render: (diplotype: string | null, row: SampleGenotyperPgx) => (
			<Text type={isNotTested(row) ? "secondary" : undefined} style={{ fontSize: 12, fontFamily: "monospace" }}>
				{diplotype || "—"}
			</Text>
		),
	},
	{
		title: "Phenotype",
		dataIndex: "phenotype",
		key: "phenotype",
		render: (phenotype: string | null, row: SampleGenotyperPgx) => {
			if (isNotTested(row)) {
				return (
					<Text type="secondary" style={{ fontSize: 12 }}>
						Not Tested
					</Text>
				);
			}
			const label = phenotype || "—";
			return isActionablePhenotype(label) ? (
				<StatusTag status="actionable" label={label} color={phenotypeColor(label)} size="small" />
			) : (
				<Text style={{ fontSize: 12 }}>{label}</Text>
			);
		},
	},
	{
		title: "Activity score",
		dataIndex: "activity_score",
		key: "activity_score",
		width: 120,
		render: (score: string | null, row: SampleGenotyperPgx) => (
			<Text type={isNotTested(row) ? "secondary" : undefined} style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
				{score || "—"}
			</Text>
		),
	},
];

const RunLine: React.FC<{ run: SampleGenotyperRunSummary | null; legacy: boolean }> = ({ run, legacy }) => {
	if (legacy) {
		return (
			<Text type="secondary" italic style={{ fontSize: 12 }} data-testid="results-genotyper-sample-legacy">
				{LEGACY_NOTE}
			</Text>
		);
	}
	if (!run) return null;

	const status = (GENOTYPER_RUN_STATUS_LABELS[run.status] ?? run.status).toLowerCase();
	const finished = run.finished_at ? formatInZone(run.finished_at, "YYYY-MM-DD HH:mm") : "";

	return (
		<Text type="secondary" style={{ fontSize: 12 }} data-testid="results-genotyper-sample-run">
			From run #{run.id} · {finished ? `${status} ${finished}` : status}
		</Text>
	);
};

const MtdnaItem: React.FC<{ id: string; value: string }> = ({ id, value }) => (
	<Text style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }} data-testid={`results-genotyper-sample-mtdna-${id}`}>
		{value}
	</Text>
);

const MtdnaBlock: React.FC<{ mtdna: SampleGenotyperMtdna }> = ({ mtdna }) => (
	<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
		<Descriptions
			size="small"
			column={3}
			colon={false}
			items={[
				{ key: "raw_r_ratio", label: "Raw R ratio", children: <MtdnaItem id="raw_r_ratio" value={formatCn(mtdna.raw_r_ratio)} /> },
				{ key: "raw_cn", label: "Raw CN", children: <MtdnaItem id="raw_cn" value={formatCn(mtdna.raw_cn)} /> },
				{ key: "lrr_cn", label: "LRR CN", children: <MtdnaItem id="lrr_cn" value={formatCn(mtdna.lrr_cn)} /> },
			]}
		/>
		<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
			<Text style={{ fontSize: 12, fontWeight: 600 }}>Batch-relative estimates</Text>
			<Text type="secondary" style={{ fontSize: 11 }}>
				{BATCH_RELATIVE_NOTE}
			</Text>
		</div>
		<Descriptions
			size="small"
			column={3}
			colon={false}
			items={[
				{ key: "scaled_cn", label: "Scaled CN", children: <MtdnaItem id="scaled_cn" value={formatCn(mtdna.scaled_cn)} /> },
				{ key: "pca_cn", label: "PCA CN", children: <MtdnaItem id="pca_cn" value={formatCn(mtdna.pca_cn)} /> },
			]}
		/>
		<Descriptions
			size="small"
			column={3}
			colon={false}
			items={[
				{ key: "n_mt_probes", label: "MT probes", children: <MtdnaItem id="n_mt_probes" value={formatCount(mtdna.n_mt_probes)} /> },
				{ key: "n_auto_probes", label: "Autosomal probes", children: <MtdnaItem id="n_auto_probes" value={formatCount(mtdna.n_auto_probes)} /> },
			]}
		/>
	</div>
);

const BlockEmpty: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
	<EmptyState title={title} description={hint} style={{ padding: "12px 0" }} />
);

const isEmptyEnvelope = (data: SampleGenotyper): boolean =>
	data.run === null && data.cnv.length === 0 && data.pgx.length === 0 && data.mtdna === null;

const GenotyperSection: React.FC<GenotyperSectionProps> = ({ sampleId }) => {
	const { data, isLoading, isError, error, refetch } = useSampleGenotyper(sampleId);

	if (!sampleId) {
		return <EmptyState title={EMPTY_TITLE} />;
	}

	if (isLoading) {
		return <InlineLoading message="Loading genotyper results…" minHeight={160} />;
	}

	if (isError || !data) {
		const notFound = axios.isAxiosError(error) && error.response?.status === 404;
		return (
			<Alert
				type="error"
				showIcon
				data-testid="results-genotyper-sample-error"
				message="Could not load genotyper results"
				description={notFound ? "The sample was not found or is not available to your account." : "labiq did not answer — try again."}
				action={
					<Button size="small" icon={<ReloadOutlined />} onClick={() => void refetch()}>
						Retry
					</Button>
				}
			/>
		);
	}

	if (isEmptyEnvelope(data)) {
		return <EmptyState title={EMPTY_TITLE} />;
	}

	const noSignal = data.run?.cnv_status === "no_signal";

	return (
		<div data-testid="results-genotyper-sample" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			<RunLine run={data.run} legacy={data.legacy} />

			<SectionCard title="CNV" subtitle="Gene-level copy number (PennCNV)" noPadding={!noSignal && data.cnv.length > 0}>
				{noSignal ? (
					<Alert type="warning" showIcon message={NO_SIGNAL_MESSAGE} data-testid="results-genotyper-sample-cnv-no-signal" />
				) : data.cnv.length > 0 ? (
					<DataTable bare columns={cnvColumns} dataSource={data.cnv} rowKey="gene" pagination={false} data-testid="results-genotyper-sample-cnv" />
				) : (
					<BlockEmpty title="No CNV results" hint={phaseHint(data.run?.cnv_status)} />
				)}
			</SectionCard>

			<SectionCard title="PGx" subtitle="Star alleles and metabolizer phenotypes (PharmCAT)" noPadding={data.pgx.length > 0}>
				{data.pgx.length > 0 ? (
					<DataTable bare columns={pgxColumns} dataSource={data.pgx} rowKey="gene" pagination={false} data-testid="results-genotyper-sample-pgx" />
				) : (
					<BlockEmpty title="No PGx results" hint={phaseHint(data.run?.pgx_status)} />
				)}
			</SectionCard>

			<SectionCard title="mtDNA-CN" subtitle="Mitochondrial DNA copy number estimate">
				{data.mtdna ? (
					<div data-testid="results-genotyper-sample-mtdna">
						<MtdnaBlock mtdna={data.mtdna} />
					</div>
				) : (
					<BlockEmpty title="No mtDNA-CN estimate" hint={phaseHint(data.run?.mtdna_status)} />
				)}
			</SectionCard>
		</div>
	);
};

export default GenotyperSection;
